import { createSlice } from "@reduxjs/toolkit";
import {
  GET_GLOBAL_ACCESS_TOKEN_SUCCESS,
  GET_GLOBAL_ACCESS_TOKEN_FAIL,
  GET_USER_ACCESS_TOKEN_SUCCESS,
  GET_USER_ACCESS_TOKEN_FAIL,
  GET_PCMS_ACCESS_TOKEN_SUCCESS,
  GET_PCMS_ACCESS_TOKEN_FAIL,
  GET_PBI_ACCESS_TOKEN_SUCCESS,
  GET_PBI_ACCESS_TOKEN_FAIL,
  TOKEN_REFRESH_START,
  TOKEN_REFRESH_SUCCESS,
  TOKEN_REFRESH_FAILED,
  TOKEN_EXPIRY_WARNING,
  GET_USER_DETAILS_SUCCESS,
  GET_USER_DETAILS_FAIL,
  GET_USER_DETAILS_EMPTY,
  LOGOUT,
} from "../constants/action-types";

// --- Token Slice ---
const tokenSlice = createSlice({
  name: "tokens",
  initialState: {
    globalAccessToken: "",
    accessToken: "",
    sessionId: "",
    isAuthenticated: null,
    isSSOAuthenticated: false,
    creationDate: "",
    loading: true,
    isRefreshing: false,
    lastRefreshAttempt: null,
    refreshError: null,
    expiryWarning: false,
    refreshCount: 0,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(GET_GLOBAL_ACCESS_TOKEN_SUCCESS, (state, { payload }) => {
        state.globalAccessToken = payload;
        state.accessToken = "";
        state.isAuthenticated = false;
        state.creationDate = "";
        state.loading = false;
      })
      .addCase(GET_USER_ACCESS_TOKEN_SUCCESS, (state, { payload }) => {
        state.accessToken = payload.accessToken;
        state.globalAccessToken = "";
        state.isAuthenticated = true;
        state.creationDate = payload.creationDate;
        state.loading = false;
        state.isSSOAuthenticated = payload.isSSOAuthenticated;
      })
      .addCase(GET_GLOBAL_ACCESS_TOKEN_FAIL, (state) => {
        state.accessToken = "";
        state.globalAccessToken = "";
        state.isAuthenticated = false;
        state.creationDate = "";
        state.loading = false;
      })
      .addCase(GET_USER_ACCESS_TOKEN_FAIL, (state) => {
        state.accessToken = "";
        state.globalAccessToken = "";
        state.isAuthenticated = false;
        state.creationDate = "";
        state.loading = false;
      })
      .addCase(TOKEN_REFRESH_START, (state) => {
        state.isRefreshing = true;
        state.refreshError = null;
        state.lastRefreshAttempt = Date.now();
      })
      .addCase(TOKEN_REFRESH_SUCCESS, (state, { payload }) => {
        state.isRefreshing = false;
        state.refreshError = null;
        state.expiryWarning = false;
        state.refreshCount += 1;
        state.lastRefreshAttempt = payload?.timestamp || Date.now();
      })
      .addCase(TOKEN_REFRESH_FAILED, (state, { payload }) => {
        state.isRefreshing = false;
        state.refreshError = payload?.error || "Token refresh failed";
        state.lastRefreshAttempt = Date.now();
      })
      .addCase(TOKEN_EXPIRY_WARNING, (state) => {
        state.expiryWarning = true;
      })
      .addCase(LOGOUT, (state) => {
        localStorage?.removeItem("pop_status");
        state.accessToken = "";
        state.globalAccessToken = "";
        state.isAuthenticated = false;
        state.isSSOAuthenticated = false;
        state.creationDate = "";
        state.loading = true;
        state.isRefreshing = false;
        state.lastRefreshAttempt = null;
        state.refreshError = null;
        state.expiryWarning = false;
        state.refreshCount = 0;
      });
  },
});

// --- PCMS Token Slice ---
const pcmsTokenSlice = createSlice({
  name: "pcmsToken",
  initialState: { pcmsAccessToken: "" },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(GET_PCMS_ACCESS_TOKEN_SUCCESS, (state, { payload }) => {
        state.pcmsAccessToken = payload;
      })
      .addCase(GET_PCMS_ACCESS_TOKEN_FAIL, (state) => {
        state.pcmsAccessToken = "";
      });
  },
});

// --- PBI Token Slice ---
const pbiTokenSlice = createSlice({
  name: "pbiToken",
  initialState: {
    pbiAccessToken: "",
    pbiTokenExpiry: null,
    pbiReportId: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(GET_PBI_ACCESS_TOKEN_SUCCESS, (state, { payload }) => {
        state.pbiAccessToken = payload.token;
        state.pbiTokenExpiry = payload.expiry;
        state.pbiReportId = payload.reportId;
      })
      .addCase(GET_PBI_ACCESS_TOKEN_FAIL, (state) => {
        state.pbiAccessToken = "";
        state.pbiTokenExpiry = null;
        state.pbiReportId = null;
      });
  },
});

// --- User Details Slice ---
const userDetailsSlice = createSlice({
  name: "userDetails",
  initialState: { details: "" },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(GET_USER_DETAILS_SUCCESS, (state, { payload }) => {
        state.details = payload;
      })
      .addCase(GET_USER_DETAILS_FAIL, (state) => {
        state.details = "";
      })
      .addCase(GET_USER_DETAILS_EMPTY, (state) => {
        state.details = "";
      });
  },
});

export const tokenReducer = tokenSlice.reducer;
export const pcmsTokenReducer = pcmsTokenSlice.reducer;
export const pbiTokenReducer = pbiTokenSlice.reducer;
export const userDetailsReducer = userDetailsSlice.reducer;
